import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/ImageCarousel.css';

const ImageCarousel = ({ images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const intervalRef = useRef(null);
  const location = useLocation();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const startInterval = () => {
    if (location.pathname === '/home' && images.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % images.length);
      }, 5000);
    }
  };

  useEffect(() => {
    startInterval();
    return () => clearInterval(intervalRef.current);
  }, [images.length, location.pathname, isMobile]);

  const handleImageClick = () => {
    clearInterval(intervalRef.current);
    setCurrentIndex(prev => (prev + 1) % images.length);
    startInterval();
  };

  if (location.pathname !== '/home' || images.length === 0) return null;

  return (
    <div className="carousel-container">
      {isMobile ? (
        // Mobile view - single image
        <div
          className="main-image-wrapper"
          onClick={handleImageClick}
          role="button"
          tabIndex={0}
          aria-label="Carousel image"
        >
          <img
            src={images[currentIndex]?.src}
            alt={images[currentIndex]?.alt || `Image ${currentIndex + 1}`}
            className="main-image"
            loading={currentIndex === 0 ? 'eager' : 'lazy'}
            decoding="async"
          />
        </div>
      ) : (
        // Desktop view - main + secondary images
        <>
          <div
            className="main-image-wrapper"
            onClick={() => handleImageClick('main')}
            role="button"
            tabIndex={0}
            aria-label="Main carousel image"
          >
            <img
              src={images[currentIndex]?.src}
              alt={images[currentIndex]?.alt || 'Main image'}
              className="main-image"
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="secondary-images-wrapper">
            <div
              className="secondary-image-container"
              onClick={() => handleImageClick('secondary1')}
              role="button"
              tabIndex={0}
              aria-label="Secondary image 1"
            >
              <img
                src={images[(currentIndex + 1) % images.length]?.src}
                alt={images[(currentIndex + 1) % images.length]?.alt || 'Secondary image 1'}
                className="secondary-image"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div
              className="secondary-image-container"
              onClick={() => handleImageClick('secondary2')}
              role="button"
              tabIndex={0}
              aria-label="Secondary image 2"
            >
              <img
                src={images[(currentIndex + 2) % images.length]?.src}
                alt={images[(currentIndex + 2) % images.length]?.alt || 'Secondary image 2'}
                className="secondary-image"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ImageCarousel;